import React, { useCallback, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useAnalyticsPageView } from '@chilecompra/react-kit/analytics'
import { Spinner, useEffectOnce } from '@chilecompra/react-kit'
import { Button, Toast, useViewport, Radio, Typography } from '@chilecompra/react-kit/components'
import {
  closeModal,
  onResetRegisterWizardState,
  openModal,
  registerCollaborationAgreementChoicesThunk,
  getCollaborationAgreementChoicesThunk,
  onGetDirectorThunk,
  onGetSurrogantThunk,
  registerBuyerThunk,
  registerFlowThunk
} from '../RegisterWizard.actions'
import {
  Hidden,
  Image,
  DesktopNavigation,
  Link,
  Instructions,
  MobileNavigation,
  Root,
  Section,
  Strong,
  Title,
  ToastContainer
} from './RegisterWizardCollaborationAgreement.styles'

import MandatoryInstitutionsModal from '../Step0/RegisterWizardWelcome.mandatoryInstitutionsModal'
import { STEP5_FIRST_MENU_OPTIONS } from '../../../config/settings/constants'
import { useRecaptcha } from '../../../modules/hooks/recaptcha'

/**
 * The RegisterWizardCollaborationAgreement' container.
 */
const RegisterWizardCollaborationAgreement = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { size } = useViewport()
  const { loading, error } = useSelector(state => state.registerWizard.collaborationAgreement)
  const organismTypes = useSelector(state => state.registerWizard.organismTypes)
  const modals = useSelector(state => state.registerWizard.modals)
  const flow = 'INICIO_PASO_5'
  const [showAdditionalOptions, setShowAdditionalOptions] = useState(false)
  const [firstChoiceValue, setFirstChoiceValue] = useState('')
  const [firstOptionalValue, setOptionalChoiceValue] = useState('')
  const { executeRecaptcha } = useRecaptcha()

  const canNotNextButtonClick = useCallback(() => {
    const hadInvalidValues = firstChoiceValue === ''
    const hadInvalidOptionalValues = firstOptionalValue === ''
    return hadInvalidValues || (firstChoiceValue === '2' ? false : hadInvalidOptionalValues)
  }, [firstChoiceValue, firstOptionalValue])

  const disabledNextButton = canNotNextButtonClick()

  const handleNext = () => {
    if (firstChoiceValue === '2') {
      batch(() => {
        dispatch(
          registerCollaborationAgreementChoicesThunk({
            firstChoiceValue,
            firstOptionalValue
          })
        )
        dispatch(onGetDirectorThunk())
        dispatch(onGetSurrogantThunk())
      })
      onChangeStep('volunteers-agreement')
    } else {
      batch(() => {
        dispatch(
          registerCollaborationAgreementChoicesThunk({
            firstChoiceValue,
            firstOptionalValue
          })
        )
        dispatch(
          registerBuyerThunk({
            executeRecaptcha,
            onSuccessCallback: () => onChangeStep('complete')
          })
        )
      })
    }
  }

  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }

  const handleOpenMandatoryInstitutionsModal = () => {
    dispatch(openModal('mandatoryInstitutions'))
  }

  const handleConfirmAction = useCallback(
    id => {
      return () => {
        dispatch(closeModal(id))
      }
    },
    [dispatch]
  )

  const handleCloseModal = useCallback(
    id => {
      return () => {
        dispatch(closeModal(id))
      }
    },
    [dispatch]
  )

  const handleFirstChoiceChange = event => {
    if (event.target.value === '1') {
      setShowAdditionalOptions(true)
      setOptionalChoiceValue('1')
    } else {
      setShowAdditionalOptions(false)
      setOptionalChoiceValue('')
    }
    setFirstChoiceValue(event.target.value)
  }

  const handleOptionalChoiceChange = event => {
    setOptionalChoiceValue(event.target.value)
  }

  useEffectOnce(() => {
    dispatch(registerFlowThunk({ flow }))
  }, [])

  useEffectOnce(() => {
    dispatch(getCollaborationAgreementChoicesThunk())
  }, [])

  const radioOptions = organismTypes.map(type => ({
    label: type.name,
    value: String(type.id)
  }))

  useAnalyticsPageView('/paso/5/convenio')

  const renderToast = () => {
    return (
      <ToastContainer role="alert" aria-live="assertive">
        <Toast
          disableIcon
          margin={size.isTiny ? '-10px 0 0 0' : size.isDownToLarge ? '-10px 0 0 0' : '23px 0 0 0'}
          severity="info"
          width={size.isTiny ? '90%' : size.isDownToLarge ? '100%' : '263px'}
        >
          <span tabIndex="0">Revisa el listado de instituciones </span>
          <Strong tabIndex="0">obligadas </Strong>
          <span tabIndex="0">a regirse por la Ley Nº 19.886</span>
          <br />
          <br />
          <Link
            tabIndex="0"
            aria-label="Ver listado"
            color="primary"
            underline
            onClick={handleOpenMandatoryInstitutionsModal}
          >
            Ver listado
          </Link>
        </Toast>
      </ToastContainer>
    )
  }

  useAnalyticsPageView('/paso/5/consideraciones')

  return (
    <Root>
      <Section
        container={!!(size.isTiny || size.isDownToLarge)}
        display="flex"
        flexDirection={size.isTiny ? 'column' : 'row'}
        justifyContent="space-between"
        gap="40px"
      >
        <Section>
          <Section margin={size.isTiny ? '0 0 32px' : '24px 0 32px'} width={size.isUpToLarge ? '552px' : '100%'}>
            <Instructions>
              <Title
                tabIndex="0"
                id="step-title"
                aria-label="Paso 5: Convenio de adhesión - ¿Tu institución está obligada a regirse por la Ley Nº 19.886, de acuerdo a su artículo 1º? "
              >
                ¿Tu institución está obligada a regirse por la Ley Nº 19.886, de acuerdo a su artículo 1º?
              </Title>
              <br />
              <Radio
                direction="vertical"
                options={[
                  {
                    label: STEP5_FIRST_MENU_OPTIONS.MANDATORY,
                    value: '1'
                  },
                  {
                    label: STEP5_FIRST_MENU_OPTIONS.NOT_MANDATORY,
                    value: '2'
                  }
                ]}
                value={firstChoiceValue}
                onChange={handleFirstChoiceChange}
                aria-labelledby="step-title"
              />
              {showAdditionalOptions && (
                <>
                  <Typography tabIndex="0" fontWeight="bold" variant="body1">
                    ¿A cuál de las siguientes categorias pertenece tu institución?
                  </Typography>
                  <br />
                  <Radio
                    direction="vertical"
                    options={radioOptions}
                    value={firstOptionalValue}
                    onChange={handleOptionalChoiceChange}
                  />
                </>
              )}
            </Instructions>
          </Section>
          {error && error === 'SOMETHING_WENT_WRONG_ERROR' && !loading && (
            <Toast
              tabIndex="0"
              width={size.isTiny ? '' : size.isDownToLarge ? '' : '70%'}
              severity="warning"
              padding="16px 24px"
            >
              Error en servicio externo, puedes intentar nuevamente en unos minutos para continuar con tu inscripción.
            </Toast>
          )}

          {(size.isTiny || size.isDownToLarge) && !loading && renderToast()}

          {loading && (
            <Section
              role="status"
              aria-label="Cargando..."
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginTop={size.isTiny ? '30px' : size.isDownToLarge ? '20px' : '40px'}
            >
              <Spinner />
            </Section>
          )}
        </Section>

        <Section display="flex" flexDirection="column">
          <Hidden lgDown>
            <Image src="/assets/img/steps/paso6.svg" alt="Inscribir una nueva institución en Mercado Público" />
          </Hidden>
          {!size.isTiny && !size.isDownToLarge && renderToast()}
        </Section>
      </Section>

      {!error && !size.isTiny && (
        <Section container margin={size.isDownToLarge || size.isTiny ? '' : '53px 0 0 0'}>
          <DesktopNavigation>
            <Button
              tabIndex="0"
              aria-label="Cancelar"
              type="button"
              variant="outlined"
              width={size.isDownToLarge ? '182px' : '156px'}
              onClick={handleBack}
            >
              Cancelar
            </Button>
            <Button
              tabIndex="0"
              aria-label={firstChoiceValue === '1' ? 'Finalizar' : 'Siguiente'}
              disabled={loading || disabledNextButton}
              color="primary"
              margin="0 0 0 30px"
              type="button"
              width={size.isDownToLarge ? '182px' : '156px'}
              onClick={handleNext}
            >
              {firstChoiceValue === '1' ? 'Finalizar' : 'Siguiente'}
            </Button>
          </DesktopNavigation>
        </Section>
      )}
      {!error && size.isTiny && (
        <MobileNavigation>
          <Button tabIndex="0" aria-label="Cancelar" type="button" variant="outlined" onClick={handleBack}>
            Cancelar
          </Button>
          <Button
            tabIndex="0"
            aria-label={firstChoiceValue === '1' ? 'Finalizar' : 'Siguiente'}
            disabled={loading || disabledNextButton}
            color="primary"
            type="button"
            onClick={handleNext}
          >
            {firstChoiceValue === '1' ? 'Finalizar' : 'Siguiente'}
          </Button>
        </MobileNavigation>
      )}

      {error && !loading && !size.isTiny && (
        <Section container margin="52px 0 0 0">
          <DesktopNavigation>
            <Button
              tabIndex="0"
              aria-label="Cancelar"
              type="button"
              variant="outlined"
              width={size.isDownToLarge ? '182px' : '156px'}
              onClick={handleBack}
            >
              Cancelar
            </Button>
          </DesktopNavigation>
        </Section>
      )}

      {error && !loading && size.isTiny && (
        <Section>
          <MobileNavigation>
            <Button tabIndex="0" aria-label="Cancelar" type="button" variant="outlined" onClick={handleBack}>
              Cancelar
            </Button>
          </MobileNavigation>
        </Section>
      )}
      <MandatoryInstitutionsModal
        open={modals.mandatoryInstitutions?.open}
        onClose={handleCloseModal('mandatoryInstitutions')}
        confirmAction={handleConfirmAction('mandatoryInstitutions')}
      />
    </Root>
  )
}

RegisterWizardCollaborationAgreement.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardCollaborationAgreement.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardCollaborationAgreement
